import React from 'react';
import Flex, { FlexProps } from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Input, InputProps } from '@mui/material';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';
import {
  SelectExcludeCheckbox,
  SelectIncludeCheckbox,
  SelectedExcludeCheckbox,
  SelectedIncludeCheckbox
} from 'src/components/SvgIcons';
import UnstyledButton from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/UnstyledFilterButton';

export type SlSidebarProps = FlexProps;
export function SlSidebar({ sx, ...rest }: SlSidebarProps) {
  return (
    <Flex
      sx={{
        width: '200px',
        ...sx
      }}
      flexDirection="column"
      gap="lg"
      {...rest}
    />
  );
}

export type SlSidebarInputProps = InputProps;
export const SlSidebarInput = ({ sx, ...rest }: SlSidebarInputProps) => {
  const stacklineTheme = useStacklineTheme();

  return (
    <Input
      sx={{
        ...{
          height: '24px',
          fontFamily: 'Roboto',
          fontWeight: '400',
          fontSize: '12px',
          border: 'none',
          padding: '0',
          borderBottom: `0.5px solid ${stacklineTheme.colors.primaryGray}`,
          color: stacklineTheme.colors.primary,

          '&::placeholder': {
            color: hexToRgba(stacklineTheme.colors.secondary, 0.5)
          },
          '&:focus': {
            outline: 'none'
          },
          '::placeholder': {
            color: hexToRgba(stacklineTheme.colors.secondary, 0.5)
          }
        },
        ...sx
      }}
      {...rest}
    />
  );
};

export type SlSidebarSectionContainerProps = FlexProps;
export function SlSidebarSectionContainer(props: SlSidebarSectionContainerProps) {
  return <Flex flexDirection="column" gap="sm" {...props} />;
}

export type SlSidebarScrollableContainerProps = FlexProps;
export function SlSidebarScrollableContainer({ ...rest }: SlSidebarScrollableContainerProps) {
  return (
    <ScrollableContainer
      scrollbarWidth="5px"
      thumbColor="#bec7d3"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      width="100%"
      maxHeight="192px"
      marginTop="16px"
      gap="12px"
      paddingRight="10px"
      sx={{ overflowX: 'hidden' }}
      {...rest}
    />
  );
}

export interface SlSidebarTitleProps {
  children: React.ReactNode;
  handleClear?: () => void;
  toggle?: 'include' | 'exclude';
  handleToggle?: (toggle: 'include' | 'exclude') => void;
}

export const SlSidebarTitle = ({ children, toggle, handleClear, handleToggle }: SlSidebarTitleProps) => {
  return (
    <Flex height="24px" alignItems="center">
      <Text variant="h6">{children}</Text>
      {handleToggle && (
        <Flex gap={6} margin="0px 10px">
          <UnstyledButton
            onClick={() => {
              if (handleToggle) {
                handleToggle('include');
              }
            }}
          >
            {toggle === 'include' ? (
              <SelectedIncludeCheckbox style={{ width: '12px', height: '12px' }} />
            ) : (
              <SelectIncludeCheckbox style={{ width: '12px', height: '12px' }} />
            )}
          </UnstyledButton>
          <UnstyledButton
            onClick={() => {
              if (handleToggle) {
                handleToggle('exclude');
              }
            }}
          >
            {toggle === 'exclude' ? (
              <SelectedExcludeCheckbox style={{ width: '12px', height: '12px' }} />
            ) : (
              <SelectExcludeCheckbox style={{ width: '12px', height: '12px' }} />
            )}
          </UnstyledButton>
        </Flex>
      )}

      {handleClear && (
        <UnstyledButton onClick={handleClear} style={{ marginLeft: handleToggle ? '0px' : '8px' }}>
          <Text variant="subtitle3">Clear</Text>
        </UnstyledButton>
      )}
    </Flex>
  );
};
